import { useCallback, useEffect, useRef, useState } from 'react';
import {
  dictationErrorMessage,
  MIC_BLOCKED_MESSAGE,
  speechRecognitionCtor,
  SpeechRecognitionLike,
  SpeechResultEventLike,
} from './core/speech';

/**
 * Dictation through the browser's speech recognition. The browser's speech service may receive the
 * audio (Google, Microsoft or Apple, unless it recognises on the device); CENTERLINE stores and logs
 * neither audio nor transcript. Nothing starts until `start()` is called from a click.
 *
 * Microphone access needs HTTPS or localhost, and any CSP or Permissions-Policy added later
 * (none today: see cdk frontend-stack and public/index.html) must allow `microphone=(self)`.
 */

interface DictationOptions {
  enabled: boolean;
  lang: string;
  /** Recognise only on the device; otherwise the browser's service is used when it must be. */
  localOnly: boolean;
  /** Aborts a running recognition when it changes. */
  projectId?: string | null;
}

export interface Dictation {
  supported: boolean;
  /** Whether the browser can recognise on the device (null until known). */
  onDevice: boolean | null;
  listening: boolean;
  interim: string;
  error: string | null;
  blocked: boolean;
  start(): void;
  /** Stops listening; words already heard are still delivered. */
  stop(): void;
  /** Stops listening and drops anything not yet delivered. */
  cancel(): void;
  clearError(): void;
}

export function useDictation(
  onFinal: (text: string) => void,
  options: DictationOptions,
): Dictation {
  const { enabled, lang, localOnly, projectId } = options;
  const ctor = enabled ? speechRecognitionCtor() : null;
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [onDevice, setOnDevice] = useState<boolean | null>(null);
  const recognizer = useRef<SpeechRecognitionLike | null>(null);
  const starting = useRef(false);
  const mounted = useRef(true);
  const finalRef = useRef(onFinal);
  finalRef.current = onFinal;
  const needsInstall = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Is on-device recognition offered? Recent Chrome only; elsewhere the answer is no.
  useEffect(() => {
    if (!ctor || typeof ctor.available !== 'function') {
      setOnDevice(ctor ? false : null);
      return undefined;
    }
    let live = true;
    ctor
      .available({ langs: [lang], processLocally: true })
      .then((status) => {
        if (!live) return;
        needsInstall.current = status === 'downloadable';
        setOnDevice(status === 'available' || status === 'downloadable');
      })
      .catch(() => live && setOnDevice(false));
    return () => {
      live = false;
    };
  }, [ctor, lang]);

  const release = useCallback(() => {
    const rec = recognizer.current;
    recognizer.current = null;
    if (!rec) return;
    rec.onresult = null;
    rec.onerror = null;
    rec.onend = null;
  }, []);

  const cancel = useCallback(() => {
    const rec = recognizer.current;
    release();
    starting.current = false;
    if (rec) {
      try {
        rec.abort();
      } catch {
        // already stopped
      }
    }
    if (mounted.current) {
      setListening(false);
      setInterim('');
    }
  }, [release]);

  const stop = useCallback(() => {
    try {
      recognizer.current?.stop();
    } catch {
      // already stopped
    }
  }, []);

  const start = useCallback(() => {
    if (!ctor || blocked || recognizer.current || starting.current) return;
    if (localOnly && !onDevice) return;
    starting.current = true;
    setError(null);

    const begin = () => {
      if (!starting.current || !mounted.current) return;
      const rec = new ctor();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = lang;
      if (onDevice) rec.processLocally = true;
      rec.onresult = (e: SpeechResultEventLike) => {
        let finals = '';
        let rest = '';
        for (let i = e.resultIndex; i < e.results.length; i += 1) {
          const result = e.results[i];
          if (result.isFinal) finals += result[0].transcript;
          else rest += result[0].transcript;
        }
        if (finals.trim()) finalRef.current(finals);
        if (mounted.current) setInterim(rest.trim());
      };
      rec.onerror = (e) => {
        const message = dictationErrorMessage(e.error);
        if (!mounted.current) return;
        if (message) setError(message);
        if (message === MIC_BLOCKED_MESSAGE) setBlocked(true);
      };
      rec.onend = () => {
        if (recognizer.current === rec) recognizer.current = null;
        starting.current = false;
        if (!mounted.current) return;
        setListening(false);
        setInterim('');
      };
      recognizer.current = rec;
      try {
        rec.start();
        starting.current = false;
        setListening(true);
      } catch {
        release();
        starting.current = false;
        setError('Dictation stopped.');
      }
    };

    if (localOnly && needsInstall.current && typeof ctor.install === 'function') {
      ctor
        .install({ langs: [lang], processLocally: true })
        .then((ok) => {
          if (ok) {
            needsInstall.current = false;
            begin();
            return;
          }
          starting.current = false;
          if (mounted.current) setError("Dictation isn't available right now.");
        })
        .catch(() => {
          starting.current = false;
          if (mounted.current) setError("Dictation isn't available right now.");
        });
    } else {
      begin();
    }
  }, [ctor, blocked, localOnly, onDevice, lang, release]);

  // A running recognition never outlives its composer or its project.
  useEffect(() => cancel, [cancel, projectId]);

  return {
    supported: !!ctor,
    onDevice,
    listening,
    interim,
    error,
    blocked,
    start,
    stop,
    cancel,
    clearError: useCallback(() => setError(null), []),
  };
}
